import { useState, useRef, useEffect } from 'react'
import { Send, Sparkles } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import toast from 'react-hot-toast'
import { onboardingHelp, type OnboardingHelpTurn } from '@/api/generateapp'

// ---------------------------------------------------------------------------
// "Ask about Twasta" chat — the READ-ONLY assistant for surfaces where no
// application is open. It answers what the platform can do, how to do
// something, and what applications the user already has; it cannot change
// anything (the backend path is a single completion with no tools). Editing an
// app is the project-scoped Ask-AI dock's job, which takes over this rail the
// moment an application is opened.
//
// ONE component, two render modes:
//   variant="inline" (default) — compact block inside a dialog (NoAppDialog).
//   variant="panel"            — fills the sidebar rail on the Workbench
//                                landing page: thread grows, input pinned.
// ---------------------------------------------------------------------------

// Curated FAQ — instant, no LLM call. Click a chip to drop a built-in answer
// into the thread. Free-text questions go to the AI assistant below.
const FAQ: { q: string; a: string }[] = [
  {
    q: 'What can I build with Twasta?',
    a: 'Complete business applications — data-entry transactions (forms with header/detail rows, validations and business logic), approval workflows, dashboards and visuals, internal smart pages and public websites. You design visually or describe what you want and AI generates the first version.',
  },
  {
    q: 'How do I create a new app?',
    a: 'Pick “Create new app”, describe what you need in plain language, and AI drafts a functional specification and a first application you can refine in the visual designers.',
  },
  {
    q: 'What is a transaction designer?',
    a: 'A visual builder for data-entry screens: define a form (and nested detail forms), columns and widgets, field/form validations, and business logic (item-change rules, cross-updates, pre/post-commit actions) that run on every surface — preview, the in-app Run view, and your deployed app.',
  },
  {
    q: 'Do I need my own AI key?',
    a: 'No — new accounts get free credits to build on Twasta’s own AI, with no setup. When they run out you can buy more credits, use your own Anthropic or OpenAI key, or switch to a free model, and your work carries straight on.',
  },
]

// Panel-mode starters. These go to the AI rather than a canned answer, because
// the interesting ones — "what do I have?" — depend on the user's real state.
const PANEL_STARTERS = [
  'What can I build with Twasta?',
  'What applications do I have?',
  'Are there sample apps?',
  'How do I add an approval?',
]

interface Msg {
  role: 'user' | 'assistant'
  content: string
  /** FAQ answers are instant/static; AI answers come from the backend. */
  fromFaq?: boolean
}

interface OnboardingHelpChatProps {
  /** Layout mode — see the header comment. */
  variant?: 'inline' | 'panel'
}

export default function OnboardingHelpChat({ variant = 'inline' }: OnboardingHelpChatProps) {
  const [messages, setMessages] = useState<Msg[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const panel = variant === 'panel'

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages, loading])

  const pickFaq = (item: { q: string; a: string }) => {
    setMessages((m) => [...m, { role: 'user', content: item.q }, { role: 'assistant', content: item.a, fromFaq: true }])
  }

  const ask = async (preset?: string) => {
    const q = (preset ?? input).trim()
    if (!q || loading) return
    if (!preset) setInput('')
    const next: Msg[] = [...messages, { role: 'user', content: q }]
    setMessages(next)
    setLoading(true)
    try {
      // Send recent AI/user turns as context (skip static FAQ answers).
      const history: OnboardingHelpTurn[] = next
        .filter((m) => !m.fromFaq)
        .slice(-6)
        .map((m) => ({ role: m.role, content: m.content }))
      const answer = await onboardingHelp(q, history.slice(0, -1))
      setMessages((m) => [...m, { role: 'assistant', content: answer }])
    } catch (e: unknown) {
      // 402 (allowance/no-key) is handled globally by AiAccessModal — don't
      // double-toast it. Surface other errors inline.
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status !== 402) {
        const msg =
          (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
          'Could not get an answer. Please try again.'
        toast.error(msg)
        setMessages((m) => [...m, { role: 'assistant', content: msg }])
      }
    } finally {
      setLoading(false)
    }
  }

  const chips = panel ? PANEL_STARTERS.map((q) => ({ q, a: '' })) : FAQ
  const empty = messages.length === 0 && !loading

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
        ...(panel ? { flex: 1, minHeight: 0 } : null),
      }}
    >
      {/* Panel mode leads with the invitation — this rail is otherwise idle. */}
      {panel && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Sparkles size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <p style={{ margin: 0, fontSize: '0.9375rem', fontWeight: 600, color: 'var(--color-text)' }}>
            Have a question about Twasta.ai?
          </p>
          {!empty && (
            /* Clearing brings the starter chips back — they're hidden while a
               thread is open so they don't crowd the answer. */
            <button
              type="button"
              onClick={() => setMessages([])}
              disabled={loading}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--color-accent)',
                cursor: loading ? 'default' : 'pointer',
                opacity: loading ? 0.5 : 1,
                whiteSpace: 'nowrap',
              }}
            >
              Clear
            </button>
          )}
        </div>
      )}
      {panel && empty && (
        <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--color-text-secondary)', flexShrink: 0 }}>
          Ask what the platform can do, how to do something, or what applications
          you already have. Open an application and this panel becomes the AI
          agent that builds and changes it for you.
        </p>
      )}

      {/* Suggested questions. In panel mode they are a starting point only —
          once the thread has content they'd push the answer out of view in a
          narrow rail, so they retire and the input carries the conversation. */}
      {(!panel || empty) && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', flexShrink: 0 }}>
        {chips.map((f) => (
          <button
            key={f.q}
            onClick={() => (panel ? ask(f.q) : pickFaq(f))}
            disabled={loading}
            style={{
              fontSize: '0.8125rem',
              padding: '0.3rem 0.6rem',
              borderRadius: '999px',
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-secondary)',
              color: 'var(--color-text-secondary)',
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.6 : 1,
              textAlign: 'left',
            }}
          >
            {f.q}
          </button>
        ))}
      </div>
      )}

      {/* Thread */}
      {(messages.length > 0 || loading) && (
        <div
          ref={scrollRef}
          style={{
            ...(panel ? { flex: 1, minHeight: 0 } : { maxHeight: 220 }),
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            padding: '0.5rem',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
          }}
        >
          {messages.map((m, i) => (
            <div
              key={i}
              style={{
                alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                maxWidth: '90%',
                fontSize: '0.875rem',
                lineHeight: 1.45,
                padding: '0.4rem 0.6rem',
                borderRadius: 'var(--radius-md)',
                background: m.role === 'user' ? 'var(--color-accent)' : 'var(--color-bg)',
                color: m.role === 'user' ? '#fff' : 'var(--color-text)',
                border: m.role === 'user' ? 'none' : '1px solid var(--color-border)',
                overflowWrap: 'anywhere',
              }}
            >
              {m.role === 'assistant' && !m.fromFaq ? (
                /* AI answers come back as markdown (bullets, bold, tables). */
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              ) : (
                <span style={{ whiteSpace: 'pre-wrap' }}>{m.content}</span>
              )}
            </div>
          ))}
          {loading && (
            <div style={{ alignSelf: 'flex-start', fontSize: '0.875rem', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Sparkles size={14} className="animate-pulse" /> Thinking…
            </div>
          )}
        </div>
      )}

      {/* Input — pinned to the bottom of the rail in panel mode. */}
      <div style={{ display: 'flex', gap: '0.5rem', ...(panel ? { marginTop: 'auto', flexShrink: 0 } : null) }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') ask() }}
          placeholder={panel ? 'Ask about Twasta.ai…' : 'Ask anything about Twasta…'}
          autoComplete="off"
          data-form-type="other"
          data-lpignore="true"
          style={{
            flex: 1,
            minWidth: 0,
            ...(panel
              ? {
                  padding: '0.5rem 0.625rem',
                  fontSize: '0.875rem',
                  background: 'var(--color-bg)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--color-text)',
                  fontFamily: 'var(--font-primary)',
                }
              : null),
          }}
          disabled={loading}
        />
        <button
          className="btn btn-primary"
          onClick={() => ask()}
          disabled={loading || !input.trim()}
          aria-label="Send"
          style={panel ? { padding: '0 0.7rem' } : undefined}
        >
          <Send size={16} />
        </button>
      </div>
    </div>
  )
}
